import { useState, useEffect, useRef } from 'react'
import { Play, Pause, RotateCcw, Settings, TrendingUp, Target, Zap, Dna, BarChart3, Save, Download } from 'lucide-react'
import { Deck, CardType, Faction } from '../types'

const fitnessHistory = [
  { generation: 1, avg: 0.42, best: 0.55 },
  { generation: 2, avg: 0.45, best: 0.58 },
  { generation: 3, avg: 0.47, best: 0.62 },
  { generation: 4, avg: 0.49, best: 0.65 },
  { generation: 5, avg: 0.51, best: 0.68 },
  { generation: 6, avg: 0.52, best: 0.70 },
  { generation: 7, avg: 0.54, best: 0.72 },
  { generation: 8, avg: 0.55, best: 0.74 },
  { generation: 9, avg: 0.56, best: 0.75 },
  { generation: 10, avg: 0.57, best: 0.76 },
]

const mockBestDeck: Deck = {
  id: 'evolved-1',
  name: 'Dragones Optimizado v3.2',
  format: 'Standard',
  isPublic: false,
  userId: 'user1',
  cards: [],
  createdAt: '2026-02-04',
  updatedAt: '2026-02-04',
}

const evolutionStats = {
  populationSize: 50,
  generations: 100,
  currentGeneration: 10,
  bestFitness: 0.76,
  avgFitness: 0.57,
  mutationsApplied: 127,
  convergence: 0.02,
  runtime: '4m 32s',
}

type EvolutionStatus = 'idle' | 'running' | 'paused' | 'completed'

export default function EvolutionLabPage() {
  const [status, setStatus] = useState<EvolutionStatus>('idle')
  const [currentGen, setCurrentGen] = useState(10)
  const [bestFitness, setBestFitness] = useState(0.76)
  const [avgFitness, setAvgFitness] = useState(0.57)
  const [params, setParams] = useState({
    populationSize: 50,
    generations: 100,
    mutationRate: 0.15,
    crossoverRate: 0.7,
    tournamentSize: 5,
    earlyStop: 20,
  })
  const [selectedBaseDeck, setSelectedBaseDeck] = useState('')
  const [showParams, setShowParams] = useState(false)

  const chartRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (status === 'running') {
      const interval = setInterval(() => {
        setCurrentGen(prev => {
          if (prev >= params.generations) {
            setStatus('completed')
            return prev
          }
          return prev + 1
        })
        setBestFitness(prev => Math.min(0.99, prev + (Math.random() * 0.02 - 0.005)))
        setAvgFitness(prev => Math.min(0.9, prev + (Math.random() * 0.01 - 0.002)))
      }, 500)
      return () => clearInterval(interval)
    }
  }, [status, params.generations])

  useEffect(() => {
    const canvas = chartRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const width = canvas.width
    const height = canvas.height
    const padding = 40

    ctx.clearRect(0, 0, width, height)

    ctx.fillStyle = '#1e293b'
    ctx.fillRect(0, 0, width, height)

    const maxFitness = 1
    const points = fitnessHistory.slice(0, currentGen)

    const avgPoints = points.map((p, i) => ({
      x: padding + (i / (params.generations - 1)) * (width - 2 * padding),
      y: height - padding - (p.avg / maxFitness) * (height - 2 * padding),
    }))

    const bestPoints = points.map((p, i) => ({
      x: padding + (i / (params.generations - 1)) * (width - 2 * padding),
      y: height - padding - (p.best / maxFitness) * (height - 2 * padding),
    }))

    ctx.strokeStyle = '#64748b'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 5])
    ctx.beginPath()
    ctx.moveTo(padding, height - padding)
    ctx.lineTo(width - padding, height - padding)
    ctx.stroke()

    ctx.setLineDash([])

    if (avgPoints.length > 1) {
      ctx.strokeStyle = '#3b82f6'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(avgPoints[0].x, avgPoints[0].y)
      avgPoints.forEach(p => ctx.lineTo(p.x, p.y))
      ctx.stroke()

      ctx.fillStyle = '#3b82f6'
      ctx.beginPath()
      avgPoints[avgPoints.length - 1].x
      ctx.arc(avgPoints[avgPoints.length - 1].x, avgPoints[avgPoints.length - 1].y, 4, 0, Math.PI * 2)
      ctx.fill()
    }

    if (bestPoints.length > 1) {
      ctx.strokeStyle = '#eab308'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(bestPoints[0].x, bestPoints[0].y)
      bestPoints.forEach(p => ctx.lineTo(p.x, p.y))
      ctx.stroke()

      ctx.fillStyle = '#eab308'
      ctx.beginPath()
      ctx.arc(bestPoints[bestPoints.length - 1].x, bestPoints[bestPoints.length - 1].y, 4, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.fillStyle = '#94a3b8'
    ctx.font = '12px Inter, system-ui'
    ctx.fillText('Generación', width / 2, height - 10)

    ctx.save()
    ctx.translate(15, height / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.fillText('Fitness', 0, 0)
    ctx.restore()
  }, [currentGen, params.generations])

  const startEvolution = () => {
    if (!selectedBaseDeck) return
    setStatus('running')
    setCurrentGen(1)
    setBestFitness(0.5)
    setAvgFitness(0.4)
  }

  const pauseEvolution = () => {
    setStatus('paused')
  }

  const resumeEvolution = () => {
    setStatus('running')
  }

  const resetEvolution = () => {
    setStatus('idle')
    setCurrentGen(1)
    setBestFitness(0.76)
    setAvgFitness(0.57)
  }

  const progress = (currentGen / params.generations) * 100

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Laboratorio de Evolución</h1>
            <p className="text-slate-400">Optimiza tus mazos usando algoritmos genéticos</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowParams(!showParams)}
              className={`flex items-center gap-2 px-4 py-2 border border-slate-600 rounded-lg transition-colors ${
                showParams ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Settings size={18} />
              Parámetros
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
              <Download size={18} />
              Exportar
            </button>
          </div>
        </div>

        {showParams && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Settings size={20} />
              Parámetros del Algoritmo Genético
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Tamaño de Población</label>
                <input
                  type="number"
                  value={params.populationSize}
                  onChange={(e) => setParams({ ...params, populationSize: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Generaciones</label>
                <input
                  type="number"
                  value={params.generations}
                  onChange={(e) => setParams({ ...params, generations: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Tasa de Mutación</label>
                <input
                  type="range"
                  min="0.01"
                  max="0.5"
                  step="0.01"
                  value={params.mutationRate}
                  onChange={(e) => setParams({ ...params, mutationRate: parseFloat(e.target.value) })}
                  className="w-full accent-yellow-500"
                />
                <span className="text-sm text-slate-400">{(params.mutationRate * 100).toFixed(0)}%</span>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Tasa de Cruzamiento</label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={params.crossoverRate}
                  onChange={(e) => setParams({ ...params, crossoverRate: parseFloat(e.target.value) })}
                  className="w-full accent-yellow-500"
                />
                <span className="text-sm text-slate-400">{(params.crossoverRate * 100).toFixed(0)}%</span>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Tamaño de Tournament</label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={params.tournamentSize}
                  onChange={(e) => setParams({ ...params, tournamentSize: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Parada Temprana (gen)</label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={params.earlyStop}
                  onChange={(e) => setParams({ ...params, earlyStop: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="bg-slate-700/50 px-6 py-4 border-b border-slate-600 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <TrendingUp size={20} />
                  Progreso de Evolución
                </h2>
                <span className="text-sm text-slate-400">
                  Generación {currentGen} / {params.generations}
                </span>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Progreso</span>
                    <span className="text-white">{progress.toFixed(1)}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4 text-center">
                    <Dna size={24} className="mx-auto mb-2 text-yellow-400" />
                    <p className="text-2xl font-bold text-white">{currentGen}</p>
                    <p className="text-xs text-slate-400">Generación Actual</p>
                  </div>
                  <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4 text-center">
                    <Target size={24} className="mx-auto mb-2 text-green-400" />
                    <p className="text-2xl font-bold text-white">{(bestFitness * 100).toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">Mejor Fitness</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4 text-center">
                    <BarChart3 size={24} className="mx-auto mb-2 text-blue-400" />
                    <p className="text-2xl font-bold text-white">{(avgFitness * 100).toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">Fitness Promedio</p>
                  </div>
                </div>

                <div className="h-64">
                  <canvas ref={chartRef} width={600} height={256} className="w-full h-full" />
                </div>

                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                    <span className="text-slate-400">Mejor Fitness</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-400 rounded-full" />
                    <span className="text-slate-400">Fitness Promedio</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-700/30 px-6 py-4 border-t border-slate-600 flex flex-wrap gap-3">
                {status === 'idle' && (
                  <>
                    <select
                      value={selectedBaseDeck}
                      onChange={(e) => setSelectedBaseDeck(e.target.value)}
                      className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="">-- Seleccionar mazo base --</option>
                      <option value="deck1">Dragones Aggro</option>
                      <option value="deck2">Demonios Control</option>
                    </select>
                    <button
                      onClick={startEvolution}
                      disabled={!selectedBaseDeck}
                      className="flex items-center gap-2 px-6 py-2 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Play size={18} />
                      Iniciar Evolución
                    </button>
                  </>
                )}
                {status === 'running' && (
                  <button
                    onClick={pauseEvolution}
                    className="flex items-center gap-2 px-6 py-2 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
                  >
                    <Pause size={18} />
                    Pausar
                  </button>
                )}
                {status === 'paused' && (
                  <button
                    onClick={resumeEvolution}
                    className="flex items-center gap-2 px-6 py-2 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
                  >
                    <Play size={18} />
                    Reanudar
                  </button>
                )}
                {(status === 'running' || status === 'paused' || status === 'completed') && (
                  <button
                    onClick={resetEvolution}
                    className="flex items-center gap-2 px-6 py-2 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-500 transition-colors"
                  >
                    <RotateCcw size={18} />
                    Reiniciar
                  </button>
                )}
                {status === 'completed' && (
                  <button className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-400 transition-colors">
                    <Save size={18} />
                    Guardar Mejor Mazo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-600">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Target size={18} />
                  Mejor Mazo Encontrado
                </h3>
              </div>
              <div className="p-4">
                <div className="bg-slate-700/30 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-medium mb-2">Dragones Optimizado v3.2</h4>
                  <p className="text-sm text-slate-400 mb-3">
                    Win Rate: <span className="text-green-400">76.2%</span> vs Meta Actual
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Consistencia</span>
                      <span className="text-white">8.5/10</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Sinergia</span>
                      <span className="text-white">9.1/10</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Resiliencia</span>
                      <span className="text-white">7.8/10</span>
                    </div>
                  </div>
                </div>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm">
                  <Download size={16} />
                  Ver Mazo Completo
                </button>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-600">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Zap size={18} />
                  Estadísticas de Carrera
                </h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Mutaciones Aplicadas</span>
                  <span className="text-white font-mono">{currentGen * 12}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Tiempo de Ejecución</span>
                  <span className="text-white font-mono">{(currentGen * 0.45).toFixed(1)}s</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Diversidad de Población</span>
                  <span className="text-white font-mono">{(0.3 + (currentGen / params.generations) * 0.4).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Convergencia</span>
                  <span className="text-yellow-400 font-mono">{(1 - currentGen / params.generations).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-600">
                <h3 className="font-semibold text-white">Sugerencias de Mejora</h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-start gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle size={12} className="text-green-400" />
                  </div>
                  <p className="text-slate-300">Considera reducir 1 Emperador Drago por más consistencia de curva</p>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Target size={12} className="text-yellow-400" />
                  </div>
                  <p className="text-slate-300">Añadir 2 Corte Solar mejora matchup vs control</p>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <TrendingUp size={12} className="text-blue-400" />
                  </div>
                  <p className="text-slate-300">El mazo tiene buena sinergia dragón-dragón</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
